/**
 * Form Field Component
 *
 * Universal form field component supporting multiple input types
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './FormField.css';

const LEARN_MORE_THRESHOLD = 120; // characters — beyond this, help text becomes a toggle

const FormField = ({ label, type, value, onChange, required, options, helpText, disabled }) => {
  const fieldId = `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const isLongHelp = helpText && helpText.length > LEARN_MORE_THRESHOLD;

  const renderInput = () => {
    switch (type) {
      case 'text':
      case 'url':
      case 'email':
        return (
          <input
            id={fieldId}
            type={type}
            className="form-field__input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <input
            id={fieldId}
            type="number"
            className="form-field__input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
            min="0"
            step="1"
          />
        );

      case 'textarea':
        return (
          <textarea
            id={fieldId}
            className="form-field__textarea"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
            rows="4"
          />
        );

      case 'dropdown':
        return (
          <select
            id={fieldId}
            className="form-field__select"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
          >
            <option value="">Select an option...</option>
            {options &&
              options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
          </select>
        );

      case 'multi-select':
        return (
          <select
            id={fieldId}
            className="form-field__select"
            value={value || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, (option) => option.value);
              onChange(selected);
            }}
            required={required}
            disabled={disabled}
            multiple
            size="5"
          >
            {options &&
              options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
          </select>
        );

      case 'date':
        return (
          <input
            id={fieldId}
            type="date"
            className="form-field__input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
          />
        );

      case 'radio':
        return (
          <div className="form-field__radio-group">
            {options &&
              options.map((option) => (
                <label key={option} className="form-field__radio-label">
                  <input
                    type="radio"
                    name={fieldId}
                    value={option}
                    checked={value === option}
                    onChange={(e) => onChange(e.target.value)}
                    required={required}
                    disabled={disabled}
                    className="form-field__radio"
                  />
                  <span className="form-field__radio-text">{option}</span>
                </label>
              ))}
          </div>
        );

      default:
        return (
          <input
            id={fieldId}
            type="text"
            className="form-field__input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className="form-field">
      <label htmlFor={fieldId} className="form-field__label">
        {label}
        {required && <span className="form-field__required">*</span>}
      </label>
      {renderInput()}
      {helpText && !isLongHelp && <p className="form-field__help">{helpText}</p>}
      {helpText && isLongHelp && (
        <div className="form-field__learn-more">
          <button
            type="button"
            className="form-field__learn-more-toggle"
            onClick={() => setLearnMoreOpen(o => !o)}
            aria-expanded={learnMoreOpen}
          >
            <span className="form-field__learn-more-icon">ℹ</span>
            {learnMoreOpen ? 'Hide detail ▲' : 'Learn more ▼'}
          </button>
          {learnMoreOpen && <p className="form-field__learn-more-text">{helpText}</p>}
        </div>
      )}
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['text', 'number', 'textarea', 'dropdown', 'multi-select', 'date', 'url', 'radio', 'email'])
    .isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  options: PropTypes.array,
  helpText: PropTypes.string,
  disabled: PropTypes.bool,
};

FormField.defaultProps = {
  required: false,
  disabled: false,
};

export default FormField;
